import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader2, Plus, MessageCircle, Trash2, Pin, Lock, Heart, MessagesSquare, Eye } from 'lucide-react';
import { api, formatApiError, absoluteUrl } from '../lib/api';
import { useAuth } from '../lib/AuthContext';
import InternalAccessGuard from '../components/InternalAccessGuard';
import useForumAccess from '../components/forum/useForumAccess';

function relativeTime(iso) {
  if (!iso) return '';
  const diff = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (diff < 60) return 'hace un instante';
  if (diff < 3600) return `hace ${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `hace ${Math.floor(diff / 3600)} h`;
  if (diff < 86400 * 7) return `hace ${Math.floor(diff / 86400)} d`;
  return new Date(iso).toLocaleDateString();
}

function CategoryInner() {
  const { lang, slug } = useParams();
  const { user } = useAuth();
  const access = useForumAccess();
  const navigate = useNavigate();
  const base = `/${lang}/${lang === 'en' ? 'forum' : 'foro'}`;
  const [category, setCategory] = useState(null);
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  async function load() {
    setLoading(true); setErr('');
    try {
      const cats = (await api.get('/forum/categories')).data || [];
      const cat = cats.find((c) => c.slug === slug);
      setCategory(cat || null);
      if (cat) {
        const th = await api.get('/forum/threads', { params: { category_id: cat.id } });
        setThreads(th.data || []);
      }
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || 'Error');
    } finally { setLoading(false); }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [slug]);

  async function removeCategory() {
    if (!window.confirm('¿Eliminar esta categoría y todos sus hilos?')) return;
    try {
      await api.delete(`/forum/categories/${category.id}`);
      navigate(base);
    } catch (e) {
      alert(formatApiError(e.response?.data?.detail));
    }
  }

  return (
    <div data-testid="forum-category-page" className="bg-gray-50 min-h-[60vh] pt-20">
      <section className="bg-white border-b border-gray-200 py-6">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to={`${base}?tab=foro`} className="inline-flex items-center gap-1.5 text-xs text-cmic-600 hover:text-cmic-700 mb-3">
            <ArrowLeft size={14} /> Foro
          </Link>
          {category && (
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div>
                <h1 className="text-2xl font-bold text-charcoal-800">{category.name}</h1>
                <p className="text-charcoal-500 text-sm mt-1">{category.description}</p>
              </div>
              <div className="flex items-center gap-2">
                {access.can_post && (
                <Link
                  to={`${base}/c/${slug}/nuevo`}
                  data-testid="forum-new-thread-btn"
                  className="inline-flex items-center gap-2 bg-cmic-500 text-white px-4 py-2 rounded-lg font-semibold text-sm hover:bg-cmic-600 transition-colors"
                >
                  <Plus size={16} /> Nuevo hilo
                </Link>
                )}
                {user && (user.role === 'admin' || category.created_by === user.id) && (
                  <button
                    onClick={removeCategory}
                    title="Eliminar categoría"
                    className="p-2 rounded-lg text-charcoal-500 hover:bg-red-50 hover:text-red-600"
                  >
                    <Trash2 size={16} />
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {err && <div className="mb-4 bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg p-3">{err}</div>}
        {loading ? (
          <div className="flex items-center justify-center py-20"><Loader2 className="animate-spin text-cmic-500" size={28} /></div>
        ) : !category ? (
          <div className="bg-white border border-gray-200 rounded-xl p-10 text-center text-charcoal-500">Categoría no encontrada.</div>
        ) : threads.length === 0 ? (
          <div className="bg-white border border-gray-200 rounded-xl p-10 text-center">
            <MessageCircle size={36} className="mx-auto text-charcoal-300 mb-3" />
            <p className="text-charcoal-500 text-sm mb-4">Aún no hay hilos en esta categoría. Sé el primero en publicar.</p>
            {access.can_post && (
            <Link to={`${base}/c/${slug}/nuevo`} className="inline-flex items-center gap-2 bg-cmic-500 text-white px-4 py-2 rounded-lg font-semibold text-sm hover:bg-cmic-600">
              <Plus size={16} /> Iniciar un debate
            </Link>
            )}
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            {threads.map((th) => (
                <Link
                  key={th.id}
                  to={`${base}/t/${th.id}`}
                  data-testid={`forum-thread-${th.id}`}
                  className="group flex items-center gap-4 p-5 bg-white rounded-2xl border border-slate-100 hover:border-cmic-200 hover:shadow-md hover:-translate-y-0.5 transition-all duration-200"
                >
                  {th.author_photo_url ? (
                    <img src={absoluteUrl(th.author_photo_url)} alt={th.author_name} loading="lazy"
                      className="w-11 h-11 rounded-2xl object-cover flex-shrink-0 ring-1 ring-slate-200 shadow-sm"
                      onError={(e) => { e.target.style.display = 'none'; }} />
                  ) : (
                    <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-cmic-500 to-cmic-700 text-white flex items-center justify-center font-semibold text-sm flex-shrink-0 shadow-sm">
                      {(th.author_name || '?').split(' ').slice(0, 2).map((n) => n[0]).join('').toUpperCase()}
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      {th.is_pinned && <Pin size={12} className="text-amber-500 flex-shrink-0" />}
                      {th.is_locked && <Lock size={12} className="text-slate-400 flex-shrink-0" />}
                      <span className="text-[15px] font-semibold text-slate-900 line-clamp-1 group-hover:text-cmic-700 transition-colors">{th.title}</span>
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5">
                      Por <strong className="text-slate-700">{th.author_name}</strong>
                      {th.author_title ? (
                        <span className="ml-1 text-[9px] font-bold uppercase tracking-wide bg-cmic-100 text-cmic-700 border border-cmic-200 px-1.5 rounded" data-testid={`forum-thread-author-title-${th.id}`}>{th.author_title}</span>
                      ) : (th.author_role === 'admin' && <span className="ml-1 text-[9px] bg-amber-100 text-amber-700 px-1 rounded">admin</span>)}
                      <span className="mx-1.5">·</span>
                      Último: {relativeTime(th.last_activity_at)}
                    </div>
                  </div>
                  <div className="flex items-center gap-3 text-slate-400">
                    {typeof th.view_count === 'number' && (
                      <span className="flex items-center gap-1 text-xs" data-testid={`forum-thread-views-${th.id}`}><Eye size={12} /> {th.view_count}</span>
                    )}
                    {(th.liked_count || (th.liked_by || []).length) > 0 && (
                      <span className="flex items-center gap-1 text-xs"><Heart size={12} /> {th.liked_count || (th.liked_by || []).length}</span>
                    )}
                    <span className="flex items-center gap-1.5 text-xs font-medium bg-slate-50 px-2.5 py-1 rounded-lg"><MessagesSquare size={13} /> {th.reply_count}</span>
                  </div>
                </Link>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

export default function ForumCategoryPage() {
  return (
    <InternalAccessGuard>
      <CategoryInner />
    </InternalAccessGuard>
  );
}
